// src/firebase.js
/*  ───────────────────────────────────────────────────────────
    Firebase 初始化 & 业务封装
    — Google/Email 登录、找回密码、白名单校验、课程 / 用户 / 成绩…
    ─────────────────────────────────────────────────────────── */
import {
  initializeApp,
  getApps,
} from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  sendPasswordResetEmail,
  fetchSignInMethodsForEmail,
  setPersistence,
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  updatePassword,
} from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
  arrayUnion,
  orderBy,
  writeBatch,
} from "firebase/firestore";
import {
  getStorage,
  ref as sRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

/* ---------- Firebase Config ---------- */
const firebaseConfig = {
  apiKey:            "AIzaSyAI8g3TQ6F5JVIaKowc8mKoxHbGso7ddKs",
  authDomain:        "liacademy.firebaseapp.com",
  projectId:         "liacademy",
  storageBucket:     "liacademy.firebasestorage.app",
  messagingSenderId: "942654861010",
  appId:             "1:942654861010:web:7ef41e2d53e5e63911c70b",
  measurementId:     "G-34WLV36GHY",
};

/* ---------- 初始化 ---------- */
const app            = initializeApp(firebaseConfig);
export const auth    = getAuth(app);
export const db      = getFirestore(app);
export const storage = getStorage(app);
export const provider= new GoogleAuthProvider();

/* 默认本地持久化 */
setPersistence(auth, browserLocalPersistence).catch(() => {});

/* ---------- 通用工具 ---------- */
const rand6 = () => Math.random().toString().slice(2, 8);
export const genStudentId  = () => `S${rand6()}`;
export const genCourseCode = () => `C${rand6()}`;
export const logout        = () => signOut(auth);

/* ===========================================================
 *  临时创建 / 更新 Auth 帐号（后台静默进行）→ 返回 uid
 * ========================================================= */
export async function createAuthUserSilently(email, password) {
  // 添加邮箱格式验证
  const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!email || !EMAIL_REGEX.test(email)) {
    console.error(`[ERROR] Invalid email format: ${email}`);
    throw new Error(`Invalid email format: ${email}`);
  }

  // 添加密码验证
  if (!password || password.length < 6) {
    console.error(`[ERROR] Password too short: ${password.length} characters`);
    throw new Error("Password must be at least 6 characters long");
  }

  const APP_NAME = "tempAdminApp";
  let tempApp;
  
  try {
    console.log(`[DEBUG] 开始创建临时 Firebase 应用...`);
    tempApp = getApps().find(a => a.name === APP_NAME)
           ?? initializeApp(firebaseConfig, APP_NAME);
    console.log(`[DEBUG] 临时应用状态:`, tempApp ? '已创建' : '创建失败');
    
    const tempAuth = getAuth(tempApp);
    console.log(`[DEBUG] 临时认证实例创建成功`);
    
    console.log(`[DEBUG] 开始创建 Firebase Auth 用户...`);
    const cred = await createUserWithEmailAndPassword(tempAuth, email, password);
    const uid = cred.user.uid;
    console.log(`[DEBUG] Firebase Auth 用户创建成功，UID: ${uid}`);
    
    // 重要：验证用户创建后的状态
    console.log(`[DEBUG] 验证用户创建状态...`);
    const user = tempAuth.currentUser;
    if (user) {
      console.log(`[DEBUG] 当前用户状态:`, {
        uid: user.uid,
        email: user.email,
        emailVerified: user.emailVerified
      });
    }
    
    // 重要：不要立即登出，让用户保持创建状态
    // 这样可以确保密码验证状态一致
    console.log(`[DEBUG] 用户创建完成，保持创建状态以确保密码验证一致性`);
    
    return uid;
  } catch (err) {
    console.error(`[ERROR] createAuthUserSilently 失败:`, {
      email,
      errorCode: err.code,
      errorMessage: err.message,
      errorStack: err.stack
    });
    
    if (err.code === "auth/email-already-in-use") {
      console.log(`[INFO] 邮箱 ${email} 已存在，返回 null`);
      return null; // UID 不可用
    } else if (err.code === "auth/invalid-email") {
      console.error(`[ERROR] 邮箱格式无效: ${email}`);
      throw new Error(`Invalid email format: ${email}`);
    } else if (err.code === "auth/weak-password") {
      console.error(`[ERROR] 密码强度不足: ${password}`);
      throw new Error("Password is too weak");
    } else {
      console.error(`[ERROR] 未知错误，抛出异常:`, err);
      throw err;
    }
  } finally {
    // 重要：不要立即删除临时应用，让用户保持创建状态
    // 这样可以确保密码验证状态一致
    // 临时应用会在下次调用时被重用或清理
    console.log(`[DEBUG] 保持临时应用状态以确保密码验证一致性`);
  }
}

/* 管理员修改家长密码时同步更新 Auth */
export async function updateAuthPasswordSilently(email, newPw) {
  const APP_NAME = "tempAdminApp";
  let tempApp;
  try {
    tempApp = getApps().find(a => a.name === APP_NAME)
           ?? initializeApp(firebaseConfig, APP_NAME);
    const tempAuth = getAuth(tempApp);
    await signInWithEmailAndPassword(tempAuth, email, newPw);
    await updatePassword(tempAuth.currentUser, newPw);
    // 重要：不要立即登出，让密码更新状态保持
    console.log(`[DEBUG] 密码更新完成，保持状态以确保一致性`);
  } catch {
    // 静默处理，避免泄漏信息；可按需记录到日志
  } finally {
    // 保持临时应用状态以确保一致性
    console.log(`[DEBUG] 保持临时应用状态以确保密码更新一致性`);
  }
}

/* 管理员删除用户时同步删除 Auth 账号 */
export async function deleteUserSilently(email, password) {
  const APP_NAME = "tempAdminApp";
  let tempApp;
  try {
    tempApp = getApps().find(a => a.name === APP_NAME)
           ?? initializeApp(firebaseConfig, APP_NAME);
    const tempAuth = getAuth(tempApp);
    
    // 登录用户
    const { user } = await signInWithEmailAndPassword(tempAuth, email, password);
    
    // 删除用户账号
    await user.delete();
    
    return true;
  } catch (err) {
    console.error("Failed to delete user from Auth:", err);
    return false;
  } finally {
    // 保持临时应用状态以确保一致性
    console.log(`[DEBUG] 保持临时应用状态以确保删除操作一致性`);
  }
}

/* 完整删除用户（Firestore + Auth） */
export async function deleteUserCompletely(uid, email) {
  try {
    // 1) 获取用户信息
    const userDoc = await getDoc(doc(db, "users", uid));
    if (!userDoc.exists()) throw new Error("User not found");

    const userData = userDoc.data() || {};
    const students = Array.isArray(userData.students) ? userData.students : [];

    // 2) 深度清理：将该用户的所有孩子从其参与的课程中彻底移除（subjects + grades + 兼容旧结构）
    for (const student of students) {
      const sid = student?.id;
      if (!sid) continue;

      // 主路径：基于 subjects 结构查找并清理
      try {
        const courses = await qUserCoursesByStudentId(sid);
        for (const c of courses) {
          await removeStudentCompletelyFromCourse(c.id, sid);
        }
      } catch (e) {
        console.warn("qUserCoursesByStudentId failed; will still run legacy cleanup:", e);
      }

      // 兜底：兼容旧结构（course.students / studentIds）
      try {
        const legacyQ = query(collection(db, "courses"), where("studentIds", "array-contains", sid));
        const legacySnap = await getDocs(legacyQ);
        if (!legacySnap.empty) {
          const batch = writeBatch(db);
          legacySnap.forEach((courseDoc) => {
            const cdata = courseDoc.data() || {};
            const nextStudents = (cdata.students || []).filter((s) => s?.id !== sid);
            const nextIds = (cdata.studentIds || []).filter((id) => id !== sid);
            batch.update(courseDoc.ref, { students: nextStudents, studentIds: nextIds });
          });
          await batch.commit();
        }
      } catch (e) {
        console.warn("Legacy cleanup failed:", e);
      }
    }

    // 3) 删除 Firebase Auth 账号（云函数）
    try {
      const token = await auth.currentUser?.getIdToken?.();
      const resp = await fetch('https://us-central1-liacademy.cloudfunctions.net/deleteUserHttp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ email })
      });
      if (!resp.ok) {
        const msg = await (async () => { try { return (await resp.json())?.message; } catch { return resp.statusText; } })();
        throw new Error(msg || `HTTP ${resp.status}`);
      }
      console.log(`User ${email} deleted from Firebase Auth via HTTP`);
    } catch (authError) {
      console.warn(`Failed to delete user from Auth: ${authError?.message || authError}`);
    }

    // 4) 删除 Firestore 用户文档
    await deleteDoc(doc(db, "users", uid));

    console.log(`User ${email} fully removed (courses cleaned, Firestore doc deleted${' '}).`);
    return true;
  } catch (error) {
    console.error("Failed to delete user completely:", error);
    throw error;
  }
}

/* ===========================================================
 *  登录 / 白名单校验
 * ========================================================= */
async function ensureWhiteListed(uid, email) {
  console.log(`[ensureWhiteListed] 开始验证用户，UID: ${uid}, 邮箱: ${email}`);
  
  try {
    // 直接通过 UID 查找用户
    console.log(`[ensureWhiteListed] 查询用户文档: users/${uid}`);
    const byUid = await getDoc(doc(db, "users", uid));
    
    if (byUid.exists()) {
      const userData = byUid.data();
      console.log(`[ensureWhiteListed] 用户文档存在:`, {
        uid: byUid.id,
        email: userData.email,
        role: userData.role,
        allow: userData.allow,
        hasStudents: !!userData.students
      });
      
      if (userData.allow) {
        console.log(`[ensureWhiteListed] 用户验证通过，返回用户信息`);
        return { uid, ...userData };
      } else {
        console.log(`[ensureWhiteListed] 用户账号未启用 (allow: false)`);
        await logout();
        throw new Error(`该账号 ${email || '未知'} 未启用，请联系管理员`);
      }
    } else {
      console.log(`[ensureWhiteListed] 用户文档不存在，UID: ${uid}`);
      await logout();
      throw new Error(`该邮箱 ${email || '未知'} 未被授权`);
    }
  } catch (error) {
    console.error(`[ensureWhiteListed] 验证过程中发生错误:`, error);
    
    // 如果不是我们抛出的错误，记录更多信息
    if (!error.message.includes('未被授权') && !error.message.includes('未启用')) {
      console.error(`[ensureWhiteListed] 原始错误详情:`, {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
    }
    
    throw error;
  }
}

export const getUserInfo = (uid, email = "") =>
  ensureWhiteListed(uid, email || auth.currentUser?.email || "");

export const loginWithGoogle = async () => {
  const { user } = await signInWithPopup(auth, provider);
  return ensureWhiteListed(user.uid, user.email);
};

export const loginWithEmailPassword = async (email, password) => {
  // 确保邮箱和密码都进行trim处理，避免空格问题
  const trimmedEmail = email.trim();
  const trimmedPassword = password.trim();
  
  console.log(`[DEBUG] 登录尝试:`, {
    originalEmail: email,
    trimmedEmail: trimmedEmail,
    originalPassword: password,
    trimmedPassword: trimmedPassword,
    passwordLength: trimmedPassword.length
  });
  
  const { user } = await signInWithEmailAndPassword(auth, trimmedEmail, trimmedPassword);
  return ensureWhiteListed(user.uid, user.email);
};

const actionCodeSettings = {
  // 密码重置邮件中的链接，用户点击后会跳转到这个页面
  url: `${window.location.origin}/login`,  // 改为跳转到登录页面
  handleCodeInApp: true,
  // 确保邮件能够正确发送
  iOS: {
    bundleId: 'com.liacademy.app'  // 如果有iOS应用的话
  },
  android: {
    packageName: 'com.liacademy.app',  // 如果有Android应用的话
    installApp: true,
    minimumVersion: '12'
  },
  // 动态链接域名（如果有的话）
  // dynamicLinkDomain: 'your-domain.page.link'
};
export const sendMagicLink = (email) =>
  sendSignInLinkToEmail(auth, email.trim(), {
    url: window.location.origin,
    handleCodeInApp: true,
  }).then(() => localStorage.setItem("magic_email", email.trim()));

export const completeMagicLinkSignIn = async (url) => {
  if (!isSignInWithEmailLink(auth, url)) return null;
  const email =
    localStorage.getItem("magic_email") || prompt("请输入登录邮箱");
  if (!email) throw new Error("email-missing");
  const { user } = await signInWithEmailLink(auth, email, url);
  localStorage.removeItem("magic_email");
  return ensureWhiteListed(user.uid, user.email);
};

/* —— 找回密码 —— */
export const sendResetIfWhitelisted = async (email) => {
  const lowerEmail = email.trim().toLowerCase();
  
  console.log(`[DEBUG] 开始处理密码重置请求，邮箱: ${lowerEmail}`);
  console.log(`[DEBUG] 当前域名: ${window.location.origin}`);
  console.log(`[DEBUG] actionCodeSettings:`, actionCodeSettings);
  
  try {
    // 通过查询 email 字段来查找用户，而不是通过 email 作为文档 ID
    console.log(`[DEBUG] 查询用户文档，邮箱: ${lowerEmail}`);
    console.log(`[DEBUG] 当前认证状态:`, auth.currentUser ? '已登录' : '未登录');
    
    // 构建查询
    const usersQuery = query(collection(db, "users"), where("email", "==", lowerEmail));
    console.log(`[DEBUG] 查询对象:`, usersQuery);
    
    // 执行查询
    console.log(`[DEBUG] 开始执行查询...`);
    const usersSnapshot = await getDocs(usersQuery);
    console.log(`[DEBUG] 查询结果:`, {
      empty: usersSnapshot.empty,
      size: usersSnapshot.size,
      docs: usersSnapshot.docs.map(doc => ({ id: doc.id, email: doc.data().email }))
    });
    
    if (usersSnapshot.empty) {
      console.log(`[DEBUG] 未找到用户，邮箱: ${lowerEmail}`);
      throw new Error("该邮箱尚未注册，请联系管理员添加到白名单");
    }
    
    const userDoc = usersSnapshot.docs[0];
    const userData = userDoc.data();
    console.log(`[DEBUG] 找到用户:`, { docId: userDoc.id, email: userData.email, allow: userData.allow, role: userData.role });
    
    if (!userData.allow) {
      console.log(`[DEBUG] 用户账号未启用: ${lowerEmail}`);
      throw new Error("该账号未启用，请联系管理员");
    }
    
    // 检查 Firebase Auth 中是否存在该用户
    console.log(`[DEBUG] 检查 Firebase Auth 用户状态...`);
    
    // 发送找回密码邮件
    console.log(`[DEBUG] 开始发送密码重置邮件到: ${lowerEmail}`);
    console.log(`[DEBUG] 使用 auth 实例:`, auth);
    console.log(`[DEBUG] 使用 actionCodeSettings:`, actionCodeSettings);
    
    // sendPasswordResetEmail 在 Firebase v9+ 中不返回值，成功时不会抛出错误
    // 如果用户不存在，Firebase 会自动抛出 auth/user-not-found 错误
    console.log(`[DEBUG] 调用 sendPasswordResetEmail，配置:`, {
      email: lowerEmail,
      actionCodeSettings: actionCodeSettings,
      currentDomain: window.location.origin
    });
    
    await sendPasswordResetEmail(auth, lowerEmail, actionCodeSettings);
    console.log(`[DEBUG] sendPasswordResetEmail 执行完成，无返回值（这是正常的）`);
    
    // 如果 sendPasswordResetEmail 成功执行，说明用户确实存在于 Firebase Auth 中
    // 不需要额外的验证，因为 Firebase 会自动处理不存在的用户
    console.log(`[DEBUG] 密码重置邮件发送成功，用户验证通过`);
    console.log(`[DEBUG] 邮件应该已发送到: ${lowerEmail}`);
    console.log(`[DEBUG] 请检查收件箱和垃圾邮件文件夹`);
    
    console.log(`[DEBUG] 密码重置邮件发送成功: ${lowerEmail}`);
    
    // 返回成功信息，包含更多调试信息
    return {
      success: true,
      email: lowerEmail,
      message: "密码重置邮件已发送，请检查收件箱（包括垃圾邮件文件夹）",
      debug: {
        timestamp: new Date().toISOString(),
        domain: window.location.origin,
        actionCodeSettings,
        userVerified: true
      }
    };
    
  } catch (error) {
    console.error(`[ERROR] 密码重置失败，邮箱: ${lowerEmail}`, error);
    console.error(`[ERROR] 错误详情:`, {
      code: error.code,
      message: error.message,
      stack: error.stack
    });
    
    // 如果是 Firestore 权限错误，提供更友好的错误信息
    if (error.code === 'permission-denied') {
      console.error(`[ERROR] Firestore 权限被拒绝，详细信息:`, {
        errorCode: error.code,
        errorMessage: error.message,
        email: lowerEmail,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        currentDomain: window.location.origin
      });
      
      // 提供更具体的错误信息
      if (error.message.includes('Missing or insufficient permissions')) {
        throw new Error("Firestore 权限不足。这可能是由于安全规则配置问题。请联系管理员检查 Firestore 规则配置。");
      } else {
        throw new Error("权限不足，无法查询用户信息。请联系管理员或稍后重试。");
      }
    }
    
    // 如果是 Firebase Auth 错误，提供更友好的错误信息
    if (error.code === 'auth/user-not-found') {
      console.error(`[ERROR] Firebase Auth 用户不存在:`, {
        email: lowerEmail,
        errorCode: error.code,
        errorMessage: error.message
      });
      throw new Error("该邮箱在 Firebase Auth 中不存在，请联系管理员检查账号状态");
    }
    
    if (error.code === 'auth/invalid-email') {
      console.error(`[ERROR] 邮箱格式无效:`, {
        email: lowerEmail,
        errorCode: error.code,
        errorMessage: error.message
      });
      throw new Error("邮箱地址格式无效，请检查输入");
    }
    
    if (error.code === 'auth/too-many-requests') {
      console.error(`[ERROR] 请求过于频繁:`, {
        email: lowerEmail,
        errorCode: error.code,
        errorMessage: error.message
      });
      throw new Error("请求过于频繁，请稍后再试");
    }
    
    if (error.code === 'auth/network-request-failed') {
      console.error(`[ERROR] 网络请求失败:`, {
        email: lowerEmail,
        errorCode: error.code,
        errorMessage: error.message
      });
      throw new Error("网络请求失败，请检查网络连接");
    }
    
    // 其他错误，抛出原始错误信息
    console.error(`[ERROR] 未知错误:`, {
      email: lowerEmail,
      errorCode: error.code,
      errorMessage: error.message,
      errorStack: error.stack
    });
    throw new Error(`密码重置失败: ${error.message} (错误代码: ${error.code || 'unknown'})`);
  }
};

/* —— 发送初始密码邮件 —— */
export const sendInitialPasswordEmail = async (email, password, role = "parent") => {
  try {
    // 导入邮件服务
    const emailService = (await import('./services/EmailService.js')).default;
    
    // 使用新的邮件服务发送邮件
    const result = await emailService.sendInitialPasswordEmail(email, password, role);
    
    console.log(`[INFO] 初始密码邮件发送成功: ${email} (模式: ${result.mode})`);
    return result;
  } catch (error) {
    console.error(`[ERROR] 初始密码邮件发送失败: ${email}`, error);
    
    // 如果邮件发送失败，在控制台显示密码（开发环境）
    if (import.meta.env.MODE === 'development') {
      console.log(`[DEV] 开发环境 - 初始密码: ${email} -> ${password}`);
      console.log(`[DEV] 邮件发送失败，请检查邮件服务配置`);
    }
    
    throw error;
  }
};


/* ===========================================================
 *  以下：Admin / Terms / Courses / Grades / Materials
 * =========================================================== */

export const upsertUser = async (email, role = "parent", firstStudentName = null, extra = {}) => {
  const lowerEmail = email.toLowerCase();
  
  console.log(`[DEBUG] upsertUser: 开始处理用户 ${lowerEmail}, 角色: ${role}`);
  console.log(`[DEBUG] 额外参数:`, extra);
  
  // 添加邮箱格式验证
  const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!lowerEmail || !EMAIL_REGEX.test(lowerEmail)) {
    console.error(`[ERROR] Invalid email format: ${lowerEmail}`);
    throw new Error(`Invalid email format: ${lowerEmail}`);
  }
  
  // 预检查：检查用户是否已存在于Firebase Auth中
  try {
    const tempApp = getApps().find((a) => a.name === "tempAdminApp")
                 ?? initializeApp(firebaseConfig, "tempAdminApp");
    const tempAuth = getAuth(tempApp);
    
    const signInMethods = await fetchSignInMethodsForEmail(tempAuth, lowerEmail);
    if (signInMethods.length > 0) {
      console.log(`[DEBUG] 用户 ${lowerEmail} 已存在于Firebase Auth中，登录方式:`, signInMethods);

      // 用户已存在，查找对应的Firestore文档
      const usersQuery = query(collection(db, "users"), where("email", "==", lowerEmail));
      const usersSnapshot = await getDocs(usersQuery);

      if (!usersSnapshot.empty) {
        const userDoc = usersSnapshot.docs[0];
        const userData = userDoc.data();
        console.log(`[DEBUG] 找到现有用户文档:`, userDoc.id, userData);

        // 更新现有用户信息：确保启用状态；对于 teacher 角色，不覆盖原有基础角色
        const nextRole = (() => {
          if (role === "teacher") {
            const cur = String(userData.role || "");
            return cur && cur !== "teacher" ? cur : "teacher";
          }
          return role;
        })();

        const updateData = {
          role: nextRole,
          allow: true,
          email: lowerEmail,
          // 标注教师身份（用于多角色场景）
          ...(role === "teacher" ? { isTeacher: true } : {}),
          ...extra,
          // 如果extra中有students，合并到现有students数组
          ...(extra.students && {
            students: arrayUnion(...extra.students)
          })
        };

        await updateDoc(userDoc.ref, updateData);
        console.log(`[DEBUG] 成功更新现有用户并设置角色为 ${role}:`, userDoc.id);

        // 保持临时应用状态
        console.log(`[DEBUG] 保持临时应用状态以确保一致性`);

        return {
          uid: userDoc.id,
          email: lowerEmail,
          role,
          existing: true
        };
      }

      // 若Firestore中没有找到文档，则通过云函数查找UID并创建文档
      try {
        const functions = getFunctions();
        const getUid = httpsCallable(functions, 'getUserUidByEmail');
        const result = await getUid({ email: lowerEmail });
        const foundUid = result?.data?.uid || null;

        if (foundUid) {
          console.log(`[DEBUG] 通过云函数获取到现有用户UID: ${foundUid}`);
          const refUid = doc(db, "users", foundUid);
          await setDoc(refUid, {
            email: lowerEmail,
            role,
            allow: true,
            createdAt: serverTimestamp(),
            ...extra,
          }, { merge: true });

          return { uid: foundUid, email: lowerEmail, role, existing: true };
        } else {
          console.warn(`[WARN] Auth中存在该邮箱，但无法获取UID；请检查云函数部署状态`);
          throw new Error("该邮箱已存在于Firebase Auth，但无法获取UID创建用户档案，请联系管理员");
        }
      } catch (err) {
        console.warn(`[WARN] 通过云函数查UID失败:`, err);
        throw err;
      }
    }
    
    // 保持临时应用状态
    console.log(`[DEBUG] 保持临时应用状态以确保一致性`);
  } catch (preCheckError) {
    console.warn(`[WARN] 预检查失败，继续创建新用户:`, preCheckError);
    // 预检查失败不影响新用户创建流程
  }
  
  const genInitPw = (mail) => {
    const base = mail.split("@")[0].replace(/[^A-Za-z0-9]/g, "");
    // 确保密码长度在6-20之间，符合Firebase要求
    let password;
    if (base.length >= 6) {
      password = base.slice(0, 20);
    } else {
      // 如果邮箱前缀太短，生成一个更安全的密码
      password = base + Math.random().toString(36).slice(2, 8);
    }
    
    // 确保密码至少6个字符
    if (password.length < 6) {
      password = password + Math.random().toString(36).slice(2, 6);
    }
    
    // 确保密码不超过20个字符
    password = password.slice(0, 20);
    
    console.log(`[DEBUG] 密码生成详情:`, {
      originalBase: mail.split("@")[0],
      cleanedBase: base,
      baseLength: base.length,
      finalPassword: password,
      passwordLength: password.length
    });
    
    return password;
  };

  const pwPlain = genInitPw(lowerEmail);
  console.log(`[DEBUG] 生成初始密码: ${pwPlain}`);
  
  console.log(`[DEBUG] 调用 createAuthUserSilently...`);
  const uid = await createAuthUserSilently(lowerEmail, pwPlain);

  if (!uid) {
    console.error(`[ERROR] createAuthUserSilently 返回 null，邮箱 ${lowerEmail} 可能已存在`);
    throw new Error("账号已存在，无法获取 UID");
  }
  
  console.log(`[DEBUG] 成功获取 UID: ${uid}`);

  const data = {
    email: lowerEmail,
    role,
    allow: true,
    // 保存初始密码以便后续群发通知（仅新建账号时写入）
    initialPassword: pwPlain,
    createdAt: serverTimestamp(),
    // 支持新的字段结构：phone, secondEmail, students
    ...(role === "teacher" ? { isTeacher: true } : {}),
    ...extra,
  };
  
  console.log(`[DEBUG] 准备创建 Firestore 文档，数据:`, data);

  // 只创建一个以 UID 为文档 ID 的用户文档
  // 密码重置功能将通过查询 email 字段来查找用户
  const refUid = doc(db, "users", uid);
  console.log(`[DEBUG] 创建 Firestore 文档引用: users/${uid}`);
  
  try {
    await setDoc(refUid, data, { merge: true });
    console.log(`[DEBUG] Firestore 文档创建成功`);
  } catch (firestoreError) {
    console.error(`[ERROR] Firestore 文档创建失败:`, firestoreError);
    throw new Error(`Firestore 文档创建失败: ${firestoreError.message}`);
  }

  // 如果extra中已经包含了students数组，则不需要再添加第一个学生
  if (role === "parent" && firstStudentName && !extra.students) {
    console.log(`[DEBUG] 添加第一个学生: ${firstStudentName}`);
    const student = { id: genStudentId(), name: firstStudentName };
    try {
      await updateDoc(refUid, {
        students: arrayUnion(student),
      });
      console.log(`[DEBUG] 学生信息添加成功:`, student);
    } catch (studentError) {
      console.error(`[ERROR] 学生信息添加失败:`, studentError);
      // 不抛出错误，因为用户创建已经成功
    }
  }

  // 停止自动发送初始密码邮件（需由管理员在 Send Email 中手动群发）

  console.log(`[DEBUG] upsertUser 完成，返回结果:`, { uid, email: lowerEmail, role, initialPassword: pwPlain });
  return { uid, email: lowerEmail, role, initialPassword: pwPlain };
};

export const addStudent = (parentDocId, name) =>
  updateDoc(doc(db, "users", parentDocId), {
    students: arrayUnion({ id: genStudentId(), name }),
  });

export const removeStudentFromAllCourses = async (child) => {
  if (!child?.id) return;
  const q   = query(collection(db, "courses"), where("studentIds", "array-contains", child.id));
  const sn  = await getDocs(q);
  if (sn.empty) return;
  const batch = writeBatch(db);
  sn.forEach((d) => {
    const data = d.data();
    batch.update(d.ref, {
      students:   (data.students || []).filter((s) => s.id !== child.id),
      studentIds: (data.studentIds || []).filter((id) => id !== child.id),
    });
  });
  await batch.commit();
};

/* —— Terms —— */
export const colTerms   = collection(db, "terms");
export const qAllTerms  = query(colTerms, orderBy("createdAt", "desc"));

export const createTerm = (name) =>
  addDoc(colTerms, { name, active: false, createdAt: serverTimestamp() });

export const setActiveTerm = async (termId) => {
  const snap  = await getDocs(colTerms);
  const batch = writeBatch(db);
  snap.forEach((d) => batch.update(d.ref, { active: d.id === termId }));
  await batch.commit();
};

export const deleteTermAndCourses = async (termId) => {
  const batch = writeBatch(db);
  batch.delete(doc(db, "terms", termId));

  const cs = await getDocs(query(collection(db, "courses"), where("term", "==", termId)));
  cs.forEach((c) => batch.delete(c.ref));
  await batch.commit();
};

/* —— Courses —— */
export const colCourses      = collection(db, "courses");
export const qAllCourses     = query(colCourses, orderBy("createdAt", "desc"));
export const qActiveCourses  = (termId) =>
  query(colCourses, where("term", "==", termId));    // 前端再 sort()
export const qCoursesByStudentId = (sid) =>
  query(colCourses, where("studentIds", "array-contains", sid));

// 重构的查询函数：从课程和subjects中查找学生参与的课程
export const qUserCoursesByStudentId = async (sid) => {
  try {
    console.log(`[DEBUG] 开始查找学生 ${sid} 的课程信息`);
    
    // 1. 获取所有课程
    const coursesSnapshot = await getDocs(collection(db, "courses"));
    const courses = [];
    
    // 2. 检查每个课程的subjects，查找学生
    for (const courseDoc of coursesSnapshot.docs) {
      const courseData = courseDoc.data();
      const courseId = courseDoc.id;
      
      console.log(`[DEBUG] 检查课程: ${courseData.name} (${courseId})`);
      
      // 3. 如果课程有subjects字段，检查每个subject
      if (courseData.subjects && Array.isArray(courseData.subjects)) {
        let studentParticipatedSubjects = []; // 学生实际参与的subjects
        let subjectStudentsMap = {}; // 每个subject的学生列表
        let subjectTeachersMap = {}; // 每个subject的教师信息 { teacherId, teacherName }
        
        for (const subjectName of courseData.subjects) {
          try {
            // 检查subject文档是否存在
            const subjectDoc = await getDoc(doc(db, "courses", courseId, "subjects", subjectName));
            
            if (subjectDoc.exists()) {
              const subjectData = subjectDoc.data();
              const studentsInSubject = subjectData.students || [];
              
              console.log(`[DEBUG] Subject ${subjectName} 有 ${studentsInSubject.length} 名学生`);
              
              // 检查该subject是否包含目标学生
              const studentInSubject = studentsInSubject.find(s => s.id === sid);
              if (studentInSubject) {
                studentParticipatedSubjects.push(subjectName);
                console.log(`[DEBUG] 在 ${subjectName} 中找到学生 ${sid}`);
              }
              
              // 保存该subject的学生列表（用于成绩表显示）
              subjectStudentsMap[subjectName] = studentsInSubject;

              // 保存该subject的教师信息（用于展示）
              subjectTeachersMap[subjectName] = {
                teacherId: subjectData.teacherId || null,
                teacherName: subjectData.teacherName || null,
              };
            } else {
              console.log(`[DEBUG] Subject ${subjectName} 文档不存在`);
            }
          } catch (error) {
            console.warn(`[DEBUG] 检查subject ${subjectName} 失败:`, error);
          }
        }
        
        // 4. 如果学生参与了至少一个subject，添加课程到结果中
        if (studentParticipatedSubjects.length > 0) {
          console.log(`[DEBUG] 学生 ${sid} 参与了课程 ${courseData.name} 的subjects: ${studentParticipatedSubjects.join(", ")}`);
          
          // 收集所有参与subjects的学生（用于成绩表显示）
          const allStudentsInCourse = [];
          studentParticipatedSubjects.forEach(subjectName => {
            const studentsInSubject = subjectStudentsMap[subjectName] || [];
            studentsInSubject.forEach(student => {
              if (!allStudentsInCourse.find(s => s.id === student.id)) {
                allStudentsInCourse.push(student);
              }
            });
          });
          
          courses.push({
            id: courseId,
            ...courseData,
            // 只包含学生实际参与的subjects
            subjects: studentParticipatedSubjects,
            // 所有参与这些subjects的学生
            students: allStudentsInCourse,
            // 每个subject的学生列表映射
            subjectStudentsMap: subjectStudentsMap,
            // 每个subject的教师信息映射
            subjectTeachersMap: subjectTeachersMap,
          });
        }
      } else {
        // 5. 兼容旧结构：检查课程文档的students字段
        const oldStudents = courseData.students || [];
        const studentInCourse = oldStudents.find(s => s.id === sid);
        
        if (studentInCourse) {
          console.log(`[DEBUG] 在旧结构中找到学生 ${sid} 在课程 ${courseData.name} 中`);
          courses.push({
            id: courseId,
            ...courseData,
            students: oldStudents
          });
        }
      }
    }
    
    console.log(`[DEBUG] 找到 ${courses.length} 门课程包含学生 ${sid}`);
    courses.forEach(course => {
      if (course.subjects) {
        console.log(`[DEBUG] - ${course.name}: ${course.subjects.join(", ")}`);
      }
    });
    
    return courses;
    
  } catch (error) {
    console.error("Error fetching user courses:", error);
    return [];
  }
};

/**
 * 查询某位教师负责的课程与科目（按 subjects 子文档中的 teacherId 关联）
 * 返回的课程对象包含：
 * - subjects: 该教师负责的科目名数组
 * - students: 这些科目的学员去重并集
 * - subjectStudentsMap: 每个科目的学生列表
 * - subjectTeachersMap: 每个科目的教师信息（teacherId/teacherName）
 */
export const qCoursesByTeacherId = async (teacherId) => {
  try {
    if (!teacherId) return [];
    const coursesSnapshot = await getDocs(collection(db, "courses"));
    const result = [];
    for (const courseDoc of coursesSnapshot.docs) {
      const courseData = courseDoc.data() || {};
      const courseId = courseDoc.id;
      const subjects = Array.isArray(courseData.subjects) ? courseData.subjects : [];
      if (subjects.length === 0) continue;

      const taughtSubjects = [];
      const subjectStudentsMap = {};
      const subjectTeachersMap = {};

      for (const subjectName of subjects) {
        const sRef = doc(db, "courses", courseId, "subjects", subjectName);
        const sSnap = await getDoc(sRef);
        if (!sSnap.exists()) continue;
        const sData = sSnap.data() || {};
        const isMine = (sData.teacherId || null) === teacherId;
        subjectTeachersMap[subjectName] = {
          teacherId: sData.teacherId || null,
          teacherName: sData.teacherName || null,
        };
        if (isMine) {
          taughtSubjects.push(subjectName);
          subjectStudentsMap[subjectName] = Array.isArray(sData.students) ? sData.students : [];
        }
      }

      if (taughtSubjects.length > 0) {
        // Merge students across taught subjects
        const allStudents = [];
        const seen = new Set();
        taughtSubjects.forEach((name) => {
          const list = subjectStudentsMap[name] || [];
          list.forEach((st) => {
            if (!seen.has(st.id)) {
              seen.add(st.id);
              allStudents.push(st);
            }
          });
        });

        result.push({
          id: courseId,
          ...courseData,
          subjects: taughtSubjects,
          students: allStudents,
          subjectStudentsMap,
          subjectTeachersMap,
        });
      }
    }
    return result;
  } catch (e) {
    console.error("qCoursesByTeacherId failed:", e);
    return [];
  }
};

export const createCourse = async (
  courseName,
  termId,
  subjectIds = [],
  subjectTeacherMap = {}
) => {
  // 将 subject 文档 ID 转为名称数组
  let subjectsToStore = subjectIds;

  try {
    const subjectsSnapshot = await getDocs(collection(db, "subjects"));
    const subjectsMap = new Map(); // id -> name
    subjectsSnapshot.docs.forEach((d) => {
      const data = d.data();
      if (data && d.id) subjectsMap.set(d.id, data.subject);
    });
    subjectsToStore = subjectIds.map((id) => subjectsMap.get(id)).filter(Boolean);

    // 预取教师信息，便于去冗（可选）
    const teacherIds = Array.from(
      new Set(
        Object.values(subjectTeacherMap).filter((tid) => typeof tid === "string" && tid)
      )
    );
    const teacherNameMap = new Map(); // id -> name
    if (teacherIds.length > 0) {
      const teachersSnap = await getDocs(collection(db, "teachers"));
      teachersSnap.docs.forEach((t) => {
        const td = t.data();
        if (teacherIds.includes(t.id)) {
          teacherNameMap.set(t.id, td?.name || "");
        }
      });
    }

    // 创建课程文档（注意：不再写入 course 级别的 teacher 字段）
    const courseRef = await addDoc(colCourses, {
      name: courseName,
      code: genCourseCode(),
      term: termId,
      subjects: subjectsToStore,
      createdAt: serverTimestamp(),
    });

    // 为每个 subject 预创建子文档，并写入任课教师（可为空）
    for (const sid of subjectIds) {
      const subjectName = subjectsMap.get(sid);
      if (!subjectName) continue;

      const teacherId = subjectTeacherMap?.[sid] || "";
      const teacherName = teacherId ? (teacherNameMap.get(teacherId) || "") : "";

      await setDoc(doc(db, "courses", courseRef.id, "subjects", subjectName), {
        name: subjectName,
        teacherId: teacherId || null,
        teacherName: teacherName || null,
        createdAt: serverTimestamp(),
      }, { merge: true });
    }

    return { id: courseRef.id, name: courseName, term: termId, subjects: subjectsToStore };
  } catch (error) {
    // 回退策略：尽量仍然创建课程文档
    console.warn("createCourse encountered an issue, attempting minimal create:", error);
    const courseRef = await addDoc(colCourses, {
      name: courseName,
      code: genCourseCode(),
      term: termId,
      subjects: Array.isArray(subjectIds) ? subjectIds : [],
      createdAt: serverTimestamp(),
    });
    return { id: courseRef.id, name: courseName, term: termId, subjects: Array.isArray(subjectIds) ? subjectIds : [] };
  }
};

export const deleteCourse = (cid) => deleteDoc(doc(db, "courses", cid));

/**
 * 深度移除学生在某门课程中的所有参与：
 * - 从该课程的所有 subjects 的 students[] 中移除该学生
 * - 从所有 subject 的 grades.*.scores 中删除该学生分数
 * - 兼容旧结构：从课程文档的 students[], studentIds[] 中移除
 * - 清理用户文档中与该课程相关的引用（students[].courses 与 users.courses[]）
 */
export async function removeStudentCompletelyFromCourse(courseId, studentId) {
  if (!courseId || !studentId) throw new Error("courseId 和 studentId 必填");

  // 读取课程，拿到 subjects
  const courseRef = doc(db, "courses", courseId);
  const courseSnap = await getDoc(courseRef);
  if (!courseSnap.exists()) throw new Error("课程不存在");
  const courseData = courseSnap.data();
  const courseName = courseData?.name || "";
  const subjects = Array.isArray(courseData?.subjects) ? courseData.subjects : [];

  // 预载本课程所有 subject 的学生列表，以便后续计算家长顶层 courses[] 的科目并集
  const subjectToStudents = new Map(); // subjectName -> Array<{id,name}>

  // 遍历每个 subject：移除学生，并清理所有成绩列中的该学生成绩
  for (const subjectName of subjects) {
    const sRef = doc(db, "courses", courseId, "subjects", subjectName);
    const sSnap = await getDoc(sRef);

    if (sSnap.exists()) {
      const sData = sSnap.data() || {};
      const currentStudents = Array.isArray(sData.students) ? sData.students : [];
      const nextStudents = currentStudents.filter((s) => s.id !== studentId);

      if (nextStudents.length !== currentStudents.length) {
        await updateDoc(sRef, { students: nextStudents });
      }

      // 保存更新后的学生列表
      subjectToStudents.set(subjectName, nextStudents);

      // 清理该 subject 下所有成绩文档的 scores[studentId]
      const gradesCol = collection(db, `courses/${courseId}/subjects/${subjectName}/grades`);
      const gradesSnap = await getDocs(gradesCol);
      if (!gradesSnap.empty) {
        const batch = writeBatch(db);
        gradesSnap.docs.forEach((g) => {
          const gd = g.data();
          if (gd?.scores && Object.prototype.hasOwnProperty.call(gd.scores, studentId)) {
            const { [studentId]: _, ...restScores } = gd.scores;
            batch.update(g.ref, { scores: restScores });
          }
        });
        // 仅当 batch 有更新时提交（writeBatch 无公开长度，提交即使无更新也安全）
        await batch.commit();
      }
    } else {
      // subject 文档不存在也记为空数组用于后续计算
      subjectToStudents.set(subjectName, []);
    }
  }

  // 兼容旧结构：清理课程文档中的 students 与 studentIds
  try {
    const legacyStudents = Array.isArray(courseData?.students) ? courseData.students : [];
    const legacyIds = Array.isArray(courseData?.studentIds) ? courseData.studentIds : [];
    const nextLegacyStudents = legacyStudents.filter((s) => s.id !== studentId);
    const nextLegacyIds = legacyIds.filter((id) => id !== studentId);
    if (nextLegacyStudents.length !== legacyStudents.length || nextLegacyIds.length !== legacyIds.length) {
      await updateDoc(courseRef, { students: nextLegacyStudents, studentIds: nextLegacyIds });
    }
  } catch {}

  // 更新用户文档：找到包含该学生的家长用户，移除该学生在该课程的引用；
  // 并根据家长的其他孩子在该课程中的参与，更新 users.courses[] 的 subjects 并集
  const usersSnap = await getDocs(collection(db, "users"));
  for (const u of usersSnap.docs) {
    const uData = u.data() || {};
    const studentsArr = Array.isArray(uData.students) ? uData.students : [];
    const idx = studentsArr.findIndex((st) => st?.id === studentId);
    if (idx === -1) continue;

    // 1) 更新该学生对象中的 courses 字段
    const targetStu = studentsArr[idx] || {};
    let nextStuCourses = targetStu.courses;
    if (Array.isArray(nextStuCourses)) {
      if (nextStuCourses.length > 0 && typeof nextStuCourses[0] === "string") {
        // 模式A：字符串数组（courseId[]）
        nextStuCourses = nextStuCourses.filter((cid) => cid !== courseId);
      } else if (nextStuCourses.length > 0 && typeof nextStuCourses[0] === "object") {
        // 模式B：对象数组（{ courseId, subjects[] }[]）
        nextStuCourses = nextStuCourses.filter((c) => c?.courseId !== courseId);
      } else {
        // 未知结构，直接移除
        nextStuCourses = [];
      }
    }

    const nextStudentsArr = [...studentsArr];
    nextStudentsArr[idx] = { ...targetStu, courses: nextStuCourses };

    // 2) 重新计算该家长在本课程上的 subjects 并集（基于其余孩子）
    const otherChildrenIds = nextStudentsArr
      .filter((s) => s?.id && s.id !== studentId)
      .map((s) => s.id);

    // 统计其余孩子在本课程中的科目并集
    const unionSubjects = new Set();
    for (const subjectName of subjects) {
      const studentsInSubject = subjectToStudents.get(subjectName) || [];
      const sidSet = new Set(studentsInSubject.map((s) => s.id));
      for (const otherSid of otherChildrenIds) {
        if (sidSet.has(otherSid)) {
          unionSubjects.add(subjectName);
          break;
        }
      }
    }

    // 更新 users.courses[] 顶层记录
    const userCourses = Array.isArray(uData.courses) ? uData.courses : [];
    const recIdx = userCourses.findIndex((c) => c?.courseId === courseId);
    let nextUserCourses = [...userCourses];
    if (unionSubjects.size === 0) {
      // 若其他孩子也不在该课程，则删除记录
      if (recIdx >= 0) nextUserCourses.splice(recIdx, 1);
    } else {
      // 否则写回并集
      if (recIdx >= 0) {
        nextUserCourses[recIdx] = {
          ...nextUserCourses[recIdx],
          courseId,
          courseName,
          subjects: Array.from(unionSubjects),
        };
      } else {
        nextUserCourses.push({ courseId, courseName, subjects: Array.from(unionSubjects), addedAt: new Date() });
      }
    }

    await updateDoc(u.ref, { students: nextStudentsArr, courses: nextUserCourses });
  }
}

/**
 * 从家长资料中彻底移除某个学生：
 * - 该学生将从其参与过的所有课程/科目中被移除，并清理成绩
 * - 家长文档的 students[] 已在 UI 层先执行 arrayRemove(child)
 * - 本函数会逐门课程调用 removeStudentCompletelyFromCourse 来更新 users.courses[] 的并集
 */
export async function removeStudentCompletelyFromSystem(parentUid, child) {
  const studentId = child?.id;
  if (!studentId) return;

  try {
    // 找到该学生参与过的所有课程（基于 subjects 结构）
    const courses = await qUserCoursesByStudentId(studentId);
    for (const c of courses) {
      await removeStudentCompletelyFromCourse(c.id, studentId);
    }

    // 兜底：确保家长的 students 数组已不包含该学生（如果 UI 未执行则这里移除）
    if (parentUid) {
      const uRef = doc(db, "users", parentUid);
      const uSnap = await getDoc(uRef);
      if (uSnap.exists()) {
        const uData = uSnap.data() || {};
        const cur = Array.isArray(uData.students) ? uData.students : [];
        const next = cur.filter((s) => s?.id !== studentId);
        if (next.length !== cur.length) {
          await updateDoc(uRef, { students: next });
        }
      }
    }
  } catch (err) {
    console.error("removeStudentCompletelyFromSystem failed:", err);
    throw err;
  }
}

/* —— Grades —— */
// 旧的grade函数已废弃，现在基于subjects管理成绩
// 每个subject有自己的grades子集合：courses/{courseId}/subjects/{subjectName}/grades

// 创建subject的grade item
export const createSubjectGradeItem = async (courseId, subjectName, title) => {
  const ref = await addDoc(collection(db, `courses/${courseId}/subjects/${subjectName}/grades`), {
    item: title,
    scores: {},
    createdAt: serverTimestamp(),
  });
  return ref.id;
};

// 保存subject的grade table
export const saveSubjectGradeTable = (courseId, subjectName, gradeId, data) =>
  setDoc(doc(db, `courses/${courseId}/subjects/${subjectName}/grades`, gradeId), data, { merge: true });

// 删除subject的grade item
export const deleteSubjectGradeItem = (courseId, subjectName, gradeId) =>
  deleteDoc(doc(db, `courses/${courseId}/subjects/${subjectName}/grades`, gradeId));

// 查询subject的grades
export const qSubjectGrades = (courseId, subjectName) =>
  collection(db, `courses/${courseId}/subjects/${subjectName}/grades`);

// 兼容性函数（已废弃，保留以避免破坏现有代码）
export const createGradeItem = async () => {
  console.warn("createGradeItem is deprecated. Use createSubjectGradeItem instead.");
  throw new Error("createGradeItem is deprecated. Please use createSubjectGradeItem with subjectName parameter.");
};

export const saveGradeTable = () => {
  console.warn("saveGradeTable is deprecated. Use saveSubjectGradeTable instead.");
  throw new Error("saveGradeTable is deprecated. Please use saveSubjectGradeTable with subjectName parameter.");
};

export const deleteGradeItem = () => {
  console.warn("deleteGradeItem is deprecated. Use deleteSubjectGradeItem instead.");
  throw new Error("deleteGradeItem is deprecated. Please use deleteSubjectGradeItem with subjectName parameter.");
};

export const qGrades = () => {
  console.warn("qGrades is deprecated. Use qSubjectGrades instead.");
  throw new Error("qGrades is deprecated. Please use qSubjectGrades with subjectName parameter.");
};

/* —— Materials (PDF) —— */
export const uploadMaterial = async (file, id = null) => {
  if (file.type !== "application/pdf") throw new Error("只允许上传 PDF");

  const matId  = id || crypto.randomUUID();
  const pdfRef = sRef(storage, `materials/${matId}.pdf`);

  /* === ① 写入自定义元数据，以便在 Storage 规则里校验 creatorUid === */
  const metadata = {
    contentType: "application/pdf",
    customMetadata: { creatorUid: auth.currentUser.uid },
  };

  await uploadBytes(pdfRef, file, metadata);
  const url = await getDownloadURL(pdfRef);

  const base = {
    title: file.name.replace(/\.pdf$/i, ""),
    pdfUrl: url,
    updatedAt: serverTimestamp(),
  };

  if (id) {
    await updateDoc(doc(db, "materials", matId), base);
  } else {
    await setDoc(doc(db, "materials", matId), {
      ...base,
      createdAt:  serverTimestamp(),
      creatorUid: auth.currentUser.uid,
    });
  }
  return matId;
};

export const deleteMaterial = async (id) => {
  await deleteDoc(doc(db, "materials", id));
  await deleteObject(sRef(storage, `materials/${id}.pdf`)).catch(() => {});
};

export const qMaterialsByUser = (uid) =>
  query(collection(db, "materials"), where("creatorUid", "==", uid));

/* —— Auth 监听 —— */
export const listenAuthChange = (cb) => onAuthStateChanged(auth, cb);
